import * as api from '@gitbook/api-client';
import type { NavigationScreenSpaceState } from '@gitbook/app-navigation';
import { getLastEdit } from '@gitbook/hive-core';
import { useHiveClient } from '@gitbook/hive-react';
import { DateShortRelative } from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { BreadcrumbItem, Breadcrumbs } from '@gitbook/spine-react';
import { getSuspense } from '@gitbook/util-suspense-cache';
import { assert } from '@gitbook/util-ts';

import { RevisionOptionsButton } from '../../../Components';
import { SpaceBreadcrumbItem } from './SpaceBreadcrumbItem';
import { SpaceChangeRequestBreadcrumbItem } from './SpaceChangeRequestBreadcrumbItem';
import { SpaceSiteBreadcrumbItem } from './SpaceSiteBreadcrumbItem';

/**
 * Breadcrumbs for a revision
 */
export function BreadcrumbsRevision(props: {
    screen: NavigationScreenSpaceState;
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
}) {
    const { screen, space, changeRequest } = props;
    return (
        <Breadcrumbs label="Space breadcrumbs">
            <SpaceSiteBreadcrumbItem space={space} />
            <SpaceBreadcrumbItem space={space} editable={false} iconOnly={Boolean(changeRequest)} />
            {changeRequest && (
                <SpaceChangeRequestBreadcrumbItem
                    space={space}
                    changeRequest={changeRequest}
                    iconOnly
                    editable
                />
            )}
            <BreadcrumbItemRevision screen={screen} space={space} changeRequest={changeRequest} />
        </Breadcrumbs>
    );
}

function BreadcrumbItemRevision(props: {
    screen: NavigationScreenSpaceState;
    space: api.Space;
    changeRequest: api.ChangeRequest | null;
}) {
    const { screen, space, changeRequest } = props;
    assert(screen.revision, 'Revision is required');
    const hiveClient = useHiveClient();
    const revision = getSuspense(
        hiveClient.store.getRevision(screen.space, screen.revision, { metadata: false })
    );

    const revisionCreationDate = revision ? getLastEdit(revision.edits)?.updatedAt : undefined;

    if (!revision || !revisionCreationDate) {
        return <BreadcrumbItem label="Version history" />;
    }

    return (
        <BreadcrumbItem>
            <div className="flex items-center gap-1">
                <div>
                    Changes from{' '}
                    <DateShortRelative value={new Date(revisionCreationDate)} onlyDate={false} />
                </div>
                <RevisionOptionsButton
                    kind="blank"
                    size="medium"
                    iconOnly
                    leadingIcon={Icon.MoreHorizontal}
                    space={space}
                    revision={revision}
                    changeRequest={changeRequest}
                    readOnly={
                        !!changeRequest &&
                        (changeRequest.status === api.ChangeRequestStatus.Archived ||
                            changeRequest.status === api.ChangeRequestStatus.Merged)
                    }
                />
            </div>
        </BreadcrumbItem>
    );
}
